import React, { useState, useEffect } from 'react';
import { X, Mail, Plus, Trash2, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface BookingRow {
  groupName: string;
  fileName: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  rooms: number;
  status?: string;
  isEdited?: boolean;
}

interface SendToHotelModalProps {
  isOpen: boolean;
  onClose: () => void;
  hotelName: string;
  bookings: BookingRow[];
  hotelId?: string;
  mode?: 'hotels' | 'vouchers'; // New prop for mode
  groupName?: string; // New prop for group name when clicking from table
  tourFileData?: any; // Tour file data passed directly from BookingsPage
  selectedHotelName?: string;
  attachments?: Array<{
    filename: string;
    mimeType: string;
    contentBase64: string;
  }>;
}

export function SendToHotelModal({ 
  isOpen, 
  onClose, 
  hotelName, 
  bookings: initialBookings, 
  hotelId,
  mode = 'hotels',
  groupName,
  tourFileData: passedTourFileData,
  selectedHotelName,
  attachments = []
}: SendToHotelModalProps) {
  const parseBookingDateValue = (dateStr: string) => {
    const normalized = formatDateForInput(dateStr || '');
    if (!normalized) return Number.NEGATIVE_INFINITY;
    const parsed = new Date(normalized).getTime();
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  };

  const sortBookingsDescending = (rows: BookingRow[]) => {
    return [...rows].sort((a, b) => parseBookingDateValue(b.checkIn) - parseBookingDateValue(a.checkIn));
  };

  const [bookings, setBookings] = useState<BookingRow[]>(initialBookings);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderEmail, setSenderEmail] = useState('operations@immerseindiatours.com');
  const [subject, setSubject] = useState(`${hotelName} - Booking Request - Series 2026`);
  const [greeting, setGreeting] = useState(`Dear Team,\n\nGreetings from Immerse India Tours!`);
  const [customMessage, setCustomMessage] = useState(
    mode === 'vouchers' 
      ? '' // Empty for vouchers 
      : `I am pleased to share the dates for our upcoming series for 2026.\n\nI would appreciate it if you could provide the following details:\n  1. Rates for single, double, and extra beds for the specified dates.\n  2. Tour leader discounted rate when the group size is less than 8 pax. For groups of 8-12 pax, a 50% discount on the agreed rate, and for 12 pax and above, one single room complimentary.\n  3. A 15-day credit facility from the receipt of bills.\n  4. Availability on the mentioned dates.\n  5. Please share your MSME certificate if you have.`
  );
  const [closing, setClosing] = useState('Please let us know if you need any additional information. We look forward to hearing from you soon.');
  const [senderName, setSenderName] = useState('Operations Team');
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [tourFileData, setTourFileData] = useState<any>(passedTourFileData || null);
  const hasPassengerAttachments = attachments.length > 0;

  // Use passed tour file data or fetch if not provided
  useEffect(() => {
    if (passedTourFileData) {
      setTourFileData(passedTourFileData);
      return;
    }
    const fetchTourFileData = async () => {
      if (mode === 'vouchers' && groupName && isOpen) {
        try {
          const token = localStorage.getItem('authToken');
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
          const response = await fetch(`${API_URL}/tourfile/search?groupName=${encodeURIComponent(groupName)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (data.success && data.data?.length > 0) {
            setTourFileData(data.data[0]);
          }
        } catch (error) {
          console.error('Error fetching tour file data:', error);
        }
      }
    };
    fetchTourFileData();
  }, [mode, groupName, isOpen, passedTourFileData]);

  useEffect(() => {
    setBookings(sortBookingsDescending(initialBookings));
  }, [initialBookings]);

  useEffect(() => {
    if (isOpen) {
      checkGoogleConnection();
    }
  }, [isOpen]);

  const checkGoogleConnection = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/hotel-emails/auth/google/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setIsGoogleConnected(data.data?.connected && !data.data?.requiresReauth);
    } catch (error) {
      console.error('Error checking Google connection:', error);
      setIsGoogleConnected(false);
    }
  };

  const connectGoogleAccount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/hotel-emails/auth/google/url`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success && data.data?.authUrl) {
        window.open(data.data.authUrl, '_blank', 'width=600,height=700');
        
        // Poll for connection status
        const interval = setInterval(async () => {
          await checkGoogleConnection();
          if (isGoogleConnected) {
            clearInterval(interval);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error getting Google auth URL:', error);
    }
  };

  const handleBookingEdit = (index: number, field: keyof BookingRow, value: any) => {
    const updatedBookings = [...bookings];
    updatedBookings[index] = {
      ...updatedBookings[index],
      [field]: value,
      isEdited: true
    };
    setBookings(updatedBookings);
  };

  const addNewRow = () => {
    setBookings(sortBookingsDescending([
      ...bookings,
      {
        groupName: '',
        fileName: '',
        checkIn: '',
        checkOut: '',
        nights: 1,
        rooms: 12,
        status: '',
        isEdited: true
      }
    ]));
  };

  const removeRow = (index: number) => {
    setBookings(bookings.filter((_, i) => i !== index));
  };

  const formatDateForInput = (dateStr: string) => {
    // Convert supported display formats to "YYYY-MM-DD"
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) return dateStr.trim();

    const slashOrDash = dateStr.trim().match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (slashOrDash) {
      const day = slashOrDash[1].padStart(2, '0');
      const month = slashOrDash[2].padStart(2, '0');
      return `${slashOrDash[3]}-${month}-${day}`;
    }

    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return '';
    
    const monthMap: {[key: string]: string} = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };
    
    const day = parts[0].padStart(2, '0');
    const month = monthMap[parts[1]] || '01';
    const year = '20' + parts[2];
    
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (isoDate: string) => {
    // Convert "2027-11-16" to "16-Nov-27"
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const getNextDayDisplay = (displayDate: string) => {
    const normalized = formatDateForInput(displayDate);
    if (!normalized) return displayDate;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return displayDate;
    date.setDate(date.getDate() + 1);
    return formatDateForDisplay(date.toISOString().slice(0, 10));
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      alert('Please enter hotel email address');
      return;
    }

    if (!isGoogleConnected) {
      alert('Please connect your Google account first');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/hotel-emails/send-to-hotel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hotelId: hotelId || '',
          hotelName,
          recipientEmail,
          senderEmail,
          subject,
          greeting,
          customMessage,
          bookings: bookings.map(b => ({
            ...b,
            checkIn: formatDateForInput(b.checkIn) || b.checkIn,
            checkOut: formatDateForInput(b.checkOut) || b.checkOut
          })).sort((a, b) => parseBookingDateValue(b.checkIn) - parseBookingDateValue(a.checkIn)),
          closing,
          senderName,
          mode: mode,
          passengerDetails: tourFileData ? (() => {
            try {
              return typeof tourFileData.passengerDetails === 'string'
                ? JSON.parse(tourFileData.passengerDetails)
                : (tourFileData.passengerDetails || []);
            } catch { return []; }
          })() : undefined,
          extensions: tourFileData ? (() => {
            try {
              const rawExtensions = typeof tourFileData.extensions === 'string'
                ? JSON.parse(tourFileData.extensions)
                : (tourFileData.extensions || []);

              if (!Array.isArray(rawExtensions)) return [];

              if (!selectedHotelName) return rawExtensions;

              return rawExtensions.filter((extension: any) =>
                String(extension?.hotelName || extension?.hotel || '').trim().toLowerCase() === selectedHotelName.trim().toLowerCase()
              );
            } catch {
              return [];
            }
          })() : undefined,
          attachments
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Draft created in Gmail! Opening Gmail...');
        
        // Open Gmail drafts in new tab
        if (data.data?.gmailUrl) {
          window.open(data.data.gmailUrl, '_blank');
        } else {
          window.open('https://mail.google.com/mail/u/0/#drafts', '_blank');
        }
        
        onClose();
      } else {
        alert('Failed to create draft: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating draft:', error);
      alert('Error creating draft email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {mode === 'vouchers' ? 'Send Voucher Details' : 'Send Booking Request to Hotel'}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">{hotelName}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition">
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Google Connection Status */}
            {!isGoogleConnected && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Google Account Not Connected</p>
                    <p className="text-sm text-amber-700">Connect your Google account to send emails</p>
                  </div>
                </div>
                <Button onClick={connectGoogleAccount} variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-100">
                  Connect Google
                </Button>
              </div>
            )}

            {/* Email Addresses */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From (Sender Email)</label>
                <Input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="operations@immerseindiatours.com"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To (Hotel Email) *</label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="reservations@hotel.com"
                  className="w-full"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Greeting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Opening Message</label>
              <Textarea
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                rows={2}
                className="w-full"
              />
            </div>

            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Requirements</label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={7}
                className="w-full font-mono text-sm"
              />
            </div>

            {/* Booking Details / Tour File Table */}
            <div>
              {mode === 'hotels' && hasPassengerAttachments && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">Passenger screenshot uploaded</p>
                  <p className="text-xs text-blue-800 mb-3">Screenshot content will be included in hotel mail body along with booking details and extension tables.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {attachments.map((attachment, index) => (
                      <div key={`${attachment.filename}-${index}`} className="rounded border border-blue-200 bg-white p-2">
                        <p className="text-xs text-gray-700 truncate mb-1">{attachment.filename}</p>
                        <img
                          src={`data:${attachment.mimeType};base64,${attachment.contentBase64}`}
                          alt={attachment.filename}
                          className="w-full h-32 object-contain rounded"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  {mode === 'vouchers' ? 'Naming/Rooming list:' : 'Booking Details'}
                </label>
                {mode === 'hotels' && (
                  <Button onClick={addNewRow} variant="outline" size="sm" className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Row
                  </Button>
                )}
              </div>
              
              {mode === 'vouchers' && tourFileData && hasPassengerAttachments ? (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-emerald-900 mb-2">Passenger screenshot uploaded</p>
                  <p className="text-xs text-emerald-800 mb-3">Naming/Rooming table is hidden because screenshot attachment will be used in the draft.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {attachments.map((attachment, index) => (
                      <div key={`${attachment.filename}-${index}`} className="rounded border border-emerald-200 bg-white p-2">
                        <p className="text-xs text-gray-700 truncate mb-1">{attachment.filename}</p>
                        <img
                          src={`data:${attachment.mimeType};base64,${attachment.contentBase64}`}
                          alt={attachment.filename}
                          className="w-full h-32 object-contain rounded"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : mode === 'vouchers' && tourFileData ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                        <tr>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Room</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Room Type</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-black">Passenger</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">DOB</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Age</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Sex</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Nationality</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Passport No.</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Date Of Issue</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Expiry Date</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Place Of Issue</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Dietary Requirements</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let passengers: any[] = [];
                          try {
                            passengers = typeof tourFileData.passengerDetails === 'string' 
                              ? JSON.parse(tourFileData.passengerDetails) 
                              : (tourFileData.passengerDetails || []);
                          } catch { passengers = []; }
                          
                          if (passengers.length === 0) {
                            return (
                              <tr>
                                <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                                  No passenger details found. Please add passenger information in the Tour File.
                                </td>
                              </tr>
                            );
                          }
                          
                          // Group Twin room passengers by room number
                          const twinRoomGroups: { [key: string]: any[] } = {};
                          const nonTwinPassengers: any[] = [];
                          let roomCounter = 1;
                          
                          passengers.forEach((p: any) => {
                            if (p.roomType === 'Twin' && p.roomNumber) {
                              if (!twinRoomGroups[p.roomNumber]) {
                                twinRoomGroups[p.roomNumber] = [];
                              }
                              twinRoomGroups[p.roomNumber].push(p);
                            } else {
                              nonTwinPassengers.push(p);
                            }
                          });
                          
                          const rows: JSX.Element[] = [];
                          
                          // Render Twin room groups
                          Object.keys(twinRoomGroups).sort().forEach((roomNum) => {
                            const roomPassengers = twinRoomGroups[roomNum];
                            if (roomPassengers.length === 2) {
                              // Two passengers sharing - show in one row
                              const p1 = roomPassengers[0];
                              const p2 = roomPassengers[1];
                              rows.push(
                                <tr key={`twin-${roomNum}`} className="border-t border-gray-200 hover:bg-gray-50">
                                  <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                  <td className="px-2 py-2 text-center">Twin</td>
                                  <td className="px-2 py-2">
                                    <div className="pb-1 border-b border-gray-300">{p1.name || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.name || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.dob ? new Date(p1.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.dob ? new Date(p2.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.age || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.age || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.sex || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.sex || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.nationality || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.nationality || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.passportNo || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.passportNo || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.issueDate ? new Date(p1.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.issueDate ? new Date(p2.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.expiryDate ? new Date(p1.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.expiryDate ? new Date(p2.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.placeOfIssue || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.placeOfIssue || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.dietaryRequirements || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.dietaryRequirements || ''}</div>
                                  </td>
                                </tr>
                              );
                              roomCounter++;
                            } else {
                              // Single passenger in Twin room or more than 2 - show individually
                              roomPassengers.forEach((p: any) => {
                                rows.push(
                                  <tr key={`passenger-${p.id || roomCounter}`} className="border-t border-gray-200 hover:bg-gray-50">
                                    <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                    <td className="px-2 py-2 text-center">{p.roomType || ''}</td>
                                    <td className="px-2 py-2">{p.name || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.dob ? new Date(p.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.age || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.sex || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.nationality || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.passportNo || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.issueDate ? new Date(p.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.placeOfIssue || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.dietaryRequirements || ''}</td>
                                  </tr>
                                );
                                roomCounter++;
                              });
                            }
                          });
                          
                          // Render non-Twin passengers
                          nonTwinPassengers.forEach((p: any) => {
                            rows.push(
                              <tr key={`passenger-${p.id || roomCounter}`} className="border-t border-gray-200 hover:bg-gray-50">
                                <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                <td className="px-2 py-2 text-center">{p.roomType || ''}</td>
                                <td className="px-2 py-2">{p.name || ''}</td>
                                <td className="px-2 py-2 text-center">{p.dob ? new Date(p.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.age || ''}</td>
                                <td className="px-2 py-2 text-center">{p.sex || ''}</td>
                                <td className="px-2 py-2 text-center">{p.nationality || ''}</td>
                                <td className="px-2 py-2 text-center">{p.passportNo || ''}</td>
                                <td className="px-2 py-2 text-center">{p.issueDate ? new Date(p.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.placeOfIssue || ''}</td>
                                <td className="px-2 py-2 text-center">{p.dietaryRequirements || ''}</td>
                              </tr>
                            );
                            roomCounter++;
                          });
                          
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : mode === 'hotels' ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Group Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Check-in</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Check-out</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Night</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Room</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Status</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-black">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking, index) => (
                      <tr key={index} className={`border-t border-gray-200 ${booking.isEdited ? 'bg-amber-50' : 'bg-white'} hover:bg-gray-50`}>
                        <td className="px-2 py-2">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.groupName}
                            onChange={(e) => handleBookingEdit(index, 'groupName', e.target.value)}
                            placeholder="Group name"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="date"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={formatDateForInput(booking.checkIn)}
                            onChange={(e) => {
                              const nextDisplay = formatDateForDisplay(e.target.value);
                              handleBookingEdit(index, 'checkIn', nextDisplay);
                              handleBookingEdit(index, 'checkOut', getNextDayDisplay(nextDisplay));
                            }}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="date"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={formatDateForInput(booking.checkOut)}
                            onChange={(e) => handleBookingEdit(index, 'checkOut', formatDateForDisplay(e.target.value))}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min="1"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.nights}
                            onChange={(e) => handleBookingEdit(index, 'nights', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min="1"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.rooms}
                            onChange={(e) => handleBookingEdit(index, 'rooms', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.status || ''}
                            onChange={(e) => handleBookingEdit(index, 'status', e.target.value)}
                            placeholder="Status"
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => removeRow(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition"
                            title="Remove row"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              ) : mode === 'vouchers' ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading tour file details...</p>
                </div>
              ) : null}
              
              {mode === 'hotels' && (
                <p className="text-xs text-gray-500 mt-2">
                  💡 Rows with yellow background have been edited
                </p>
              )}
            </div>

            {/* Closing Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Closing Message</label>
              <Textarea
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                rows={2}
                className="w-full"
              />
            </div>

            {/* Sender Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sender Name</label>
              <Input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full"
                placeholder="Your Name"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {isGoogleConnected ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Google Connected
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  Not Connected
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending || !isGoogleConnected} className="bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 font-medium flex items-center gap-2">
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating Draft...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Create Draft in Gmail</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
